<template>
  <LoadingCard :loading="loading && !data" class="px-6 py-4">
    <div class="panel-metric-header mb-3">
      <div class="panel-metric-header__title">
        <h3>{{ __('Daily signal outcomes') }}</h3>
      </div>

      <SelectControl
        class="panel-metric-header__select"
        variant="compact"
        size="xs"
        :bordered="false"
        :options="ranges"
        :selected="days"
        :aria-label="__('Select Ranges')"
        @change="select"
      />
    </div>

    <ul class="mb-2 flex flex-wrap gap-x-5 text-xs">
      <li v-for="item in legend" :key="item.key">
        <span class="mr-2 inline-block h-2 w-2 rounded-full" :style="{ backgroundColor: item.color }" />{{ item.label }}
        ({{ item.total }})
      </li>
    </ul>

    <p v-if="error" class="text-sm text-red-500">{{ error }}</p>
    <div ref="chart" class="pdoc-chart ct-chart" dir="ltr" />
  </LoadingCard>
</template>

<script>
import Chartist from 'chartist'
import 'chartist/dist/chartist.min.css'

const DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const persian = value => String(value ?? '').replace(/\d/g, d => DIGITS[Number(d)])

export default {
  props: ['card'],

  data() {
    return { days: '14', data: null, loading: true, error: null, chartist: null }
  },

  computed: {
    ranges() {
      return [
        { label: this.__('Last 7 days'), value: '7' },
        { label: this.__('Last 14 days'), value: '14' },
        { label: this.__('Last 30 days'), value: '30' },
      ]
    },

    series() {
      const days = this.data?.days || []

      return {
        labels: days.map(day => persian(day.label)),
        series: [days.map(d => d.profit), days.map(d => d.loss)],
      }
    },

    legend() {
      const days = this.data?.days || []
      const sum = key => days.reduce((total, day) => total + day[key], 0)

      return [
        { key: 'profit', label: this.__('Take-profit'), color: '#22c55e', total: sum('profit') },
        { key: 'loss', label: this.__('Stop-loss'), color: '#ef4444', total: sum('loss') },
      ]
    },
  },

  watch: {
    series() {
      this.render()
    },
  },

  created() {
    this.load()
  },

  mounted() {
    this.render()
  },

  methods: {
    render() {
      if (!this.$refs.chart) return

      const options = {
        height: 190,
        seriesBarDistance: 14,
        axisY: { onlyInteger: true, offset: 28, low: 0 },
        axisX: { showGrid: false },
        chartPadding: { top: 16, right: 8, bottom: 0, left: 0 },
      }

      if (this.chartist) {
        this.chartist.update(this.series, options)

        return
      }

      this.chartist = new Chartist.Bar(this.$refs.chart, this.series, options)

      // Colour by series from the draw event: the panel's chart stylesheet outranks plain CSS, and a
      // zero day must draw nothing (a round cap on a zero-length bar shows up as a dot).
      const colors = ['#22c55e', '#ef4444']
      this.chartist.on('draw', context => {
        if (context.type !== 'bar') return

        const value = context.series[context.index]
        context.element.attr({
          style: value > 0
            ? `stroke: ${colors[context.seriesIndex]} !important; stroke-width: 9px; stroke-linecap: round;`
            : 'stroke-width: 0 !important;',
        })
      })
    },

    select(event) {
      this.days = String(event?.target?.value || event)
      this.load()
    },

    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await Nova.request().get('/nova-vendor/coins/main-point-outcomes', { params: { days: this.days } })
        this.data = data
      } catch (e) {
        this.error = this.__('Could not load the insights.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style>
.pdoc-chart .ct-label { font-size: 11px; color: #8a91a5; fill: #8a91a5; }
.pdoc-chart .ct-grid { stroke: rgba(148, 163, 184, 0.25); stroke-dasharray: 3px; }
html.dark .pdoc-chart .ct-label { color: #a1a1aa; fill: #a1a1aa; }
html.dark .pdoc-chart .ct-grid { stroke: rgba(255, 255, 255, 0.1); }
</style>
